import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { formatDate } from '@/lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ArrowRight, Megaphone } from 'lucide-react';
import { DashboardShell } from './DashboardShell';
import { useAnnouncements } from '@/hooks/dashboard/useAnnouncements';
import { AccountingAttentionRequired } from './AccountingAttentionRequired';
import { FinancialOperations } from './FinancialOperations';
import { AccountingSnapshot } from './AccountingSnapshot';

/**
 * AccountingDashboard — Command Center for Accounting-role users.
 *
 * Phase 20.7 introduces three new bands at the top:
 *   1. AccountingAttentionRequired — Pending Payment Info / Pending Reviews /
 *      Outstanding Requests / System Notifications with zero-count omission
 *      and empty state.
 *   2. FinancialOperations         — ledger operational summary (counts only,
 *      no $ totals, no revenue signals).
 *   3. AccountingSnapshot          — 4 executive-signal cells with per-metric
 *      source hints.
 *
 * Below the new bands, the pre-Phase-20.7 announcements block is preserved
 * as-is — every accounting user still sees active company announcements.
 * No Production / Sales / Revenue / Commission / Performance-ranking
 * concepts are introduced anywhere on this dashboard (Phase 20.7 guardrail).
 */
export const AccountingDashboard = () => {
  const { user } = useAuth();
  const { announcements } = useAnnouncements();

  return (
    <DashboardShell
      title="Accounting Dashboard"
      description="Manage financial operations, payment information, and administrative tasks."
    >
      <AccountingAttentionRequired />

      <FinancialOperations />

      <AccountingSnapshot />

      {announcements.length > 0 && (
        <Card className="mb-8 animate-slideIn border-l-4 border-l-blue-500" data-testid="admin-announcements">
          <CardHeader className="flex flex-row items-center justify-between py-3">
            <CardTitle className="text-base font-heading flex items-center gap-2">
              <Megaphone size={16} className="text-blue-500" />
              Announcements
            </CardTitle>
            <Link to="/announcements"><Button variant="ghost" size="sm" className="text-xs">View All <ArrowRight size={12} className="ml-1" /></Button></Link>
          </CardHeader>
          <CardContent className="pt-0 space-y-2">
            {announcements.map((ann) => (
              <div key={ann.announcement_id} className="p-3 bg-blue-50/50 rounded-lg border border-blue-100">
                <p className="text-sm font-medium text-slate-900">{ann.title}</p>
                <p className="text-xs text-slate-600 line-clamp-2 mt-0.5">{ann.message}</p>
                <p className="text-[10px] text-slate-400 mt-1">{formatDate(ann.publish_date)}</p>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </DashboardShell>
  );
};

export default AccountingDashboard;
